import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search as SearchIcon, Upload as UploadIcon, Bell, Menu, LogOut, User, Settings, LayoutDashboard, Crown, ShieldCheck, KeyRound } from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from './ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import Sidebar from './Sidebar';
import Logo from './Logo';
import RoleBadge from './RoleBadge';
import PwaInstallButton from './PwaInstallButton';
import ThemeToggle from './ThemeToggle';
import ChangePasswordDialog from './ChangePasswordDialog';
import TopNav from './TopNav';

export default function Navbar() {
  const { user, logout } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  // Auto-close mobile sidebar when the route changes
  React.useEffect(() => { setSheetOpen(false); }, [loc.pathname, loc.search]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-40 h-14 theme-navbar">
      <div className="h-full px-3 md:px-6 flex items-center gap-3">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden p-2 rounded-md hover:bg-white/5" aria-label="Menu" data-testid="nav-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-[var(--theme-surface)] border-white/10 p-0 w-64">
            <VisuallyHidden><SheetTitle>Navigation</SheetTitle></VisuallyHidden>
            <div className="pt-4"><Sidebar mobile onNavigate={() => setSheetOpen(false)} /></div>
          </SheetContent>
        </Sheet>

        <Link to="/home" className="flex items-center gap-2 shrink-0">
          <Logo />
        </Link>

        <TopNav />

        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-md ml-auto lg:ml-2">
          <div className="flex w-full border border-white/10 rounded-full overflow-hidden bg-white/[0.03] focus-within:border-[#ff2e63]/60">
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              className="flex-1 px-4 py-2 bg-transparent text-[13.5px] placeholder:text-white/40 focus:outline-none"
              placeholder="Search videos, channels, tracks…" />
            <button type="submit" className="px-4 bg-white/[0.04] hover:bg-white/[0.08] transition-colors" aria-label="Search">
              <SearchIcon className="w-4 h-4 text-white/80" />
            </button>
          </div>
        </form>

        <div className="md:hidden flex-1" />

        <div className="flex items-center gap-1 md:gap-2">
          <Link to="/search" className="md:hidden p-2 rounded-md hover:bg-white/5" aria-label="Search">
            <SearchIcon className="w-5 h-5" />
          </Link>
          <Link to="/upload" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-[13px] font-semibold">
            <UploadIcon className="w-4 h-4" /> Upload
          </Link>
          <PwaInstallButton variant="compact" testid="navbar-pwa-install" />
          <ThemeToggle />
          <button className="p-2 rounded-md hover:bg-white/5" aria-label="Notifications"><Bell className="w-5 h-5" /></button>

          <DropdownMenu>
            <DropdownMenuTrigger className="outline-none">
              <div className={`w-9 h-9 rounded-full bg-gradient-to-br from-[#ff2e63] to-[#22d3ee] flex items-center justify-center text-[14px] font-black cursor-pointer hover:ring-2 ring-white/20 transition-all ${user?.role === 'owner' ? 'ring-2 ring-[#ffcf52]' : user?.role === 'admin' ? 'ring-2 ring-white/35' : ''}`}>
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[var(--theme-surface)] border-white/10 text-[var(--theme-fg)] w-56">
              <DropdownMenuLabel>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold truncate">{user?.name}</div>
                    <div className="text-[11px] text-[var(--theme-muted)] truncate">{user?.email}</div>
                  </div>
                  {user?.role && <RoleBadge role={user.role} size="sm" />}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={() => nav('/channel/' + (user?.channelId || 'c1'))} className="gap-2"><User className="w-4 h-4" /> Your channel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav('/studio')} className="gap-2"><LayoutDashboard className="w-4 h-4" /> Creator Studio</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav('/memberships')} className="gap-2"><Settings className="w-4 h-4" /> Plan &amp; Billing</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPwdOpen(true)} className="gap-2" data-testid="menu-change-password"><KeyRound className="w-4 h-4" /> Change password</DropdownMenuItem>
              {user?.role === 'admin' && (
                <DropdownMenuItem onClick={() => nav('/admin')} className="gap-2"><ShieldCheck className="w-4 h-4" /> Admin</DropdownMenuItem>
              )}
              {user?.role === 'owner' && (
                <>
                  <DropdownMenuItem onClick={() => nav('/admin')} className="gap-2"><ShieldCheck className="w-4 h-4" /> Admin</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav('/owner')} className="gap-2"><Crown className="w-4 h-4 text-[#ffcf52]" /> <span className="text-[#ffcf52]">Owner dashboard</span></DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={() => { logout(); nav('/'); }} className="gap-2 text-[#ff6b6b]"><LogOut className="w-4 h-4" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </header>
  );
}
